// Third Party
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { get, maxBy, minBy } from 'lodash';
import moment from 'moment';

// Components
import ComparisonBaseCell from '../../../../../ComparisonTable/ComparisonBaseCell';
import HistoricalScenariosComparisonHeader from './HistoricalScenariosComparisonHeader';
import { PercentageCell, StatCell, UpDownCell } from '@/views/components/cells';

// Types
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Hooks
import { formatMessage, formatMessageWithLocale } from '@/utils/i18n';
import { useHistoricalScenarioAPIData } from '../../../../hooks/useHistoricalScenarioAPIData';
import { useSelector } from 'react-redux';
import { selectSelectedHistoricalScenarios } from '@/redux/modules/comparison/selectors';
import { DISCLAIMER_KEYS } from '@/utils/disclaimers/constants';
import calculateStartAndEndDates from '@/utils/historical-scenarios/start-and-end-date';

type Props = {
  className?: string;
  comparison: Comparison;
  item: ComparisonItem;
  user?: Nullable<User>;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
};

const HistoricalScenariosComparisonScenario: React.FC<Props> = ({
  className,
  comparison,
  item,
  user,
  updateComparisonItemColor,
  popoverContainerRef,
  scrollbarContainerRef,
}) => {
  const { calculatedHistoricalScenarios: apiCalculatedHistoricalScenarios } = useHistoricalScenarioAPIData();
  const selectedHistoricalScenarios = useSelector(selectSelectedHistoricalScenarios);

  const [calculatedHistoricalScenarios, setCalculatedHistoricalScenarios] = React.useState<any>([]);
  useEffect(() => {
    setCalculatedHistoricalScenarios(apiCalculatedHistoricalScenarios);
  }, [apiCalculatedHistoricalScenarios]);

  const itemIndex = comparison.items.findIndex((comparisonItem: any) => comparisonItem._id === item._id);
  const hasPerformanceData =
    calculatedHistoricalScenarios && calculatedHistoricalScenarios.length > 0 && selectedHistoricalScenarios && selectedHistoricalScenarios.length > 0;
  const isValidIndex = hasPerformanceData && itemIndex >= 0 && itemIndex < (comparison.items.length || 0);
  const itemPerformance =
    hasPerformanceData && isValidIndex ? calculatedHistoricalScenarios![0].items.find((historicalItem: any) => item._id === historicalItem._id) : undefined;

  const { startDate, endDate } = calculateStartAndEndDates(selectedHistoricalScenarios);

  const formattedStartingDate = moment.utc(startDate).format(formatMessage('Common.DateFormats.Short'));
  const formattedEndingDate = moment.utc(endDate).format(formatMessage('Common.DateFormats.Short'));
  const disclaimer = formatMessage('Disclaimers.HistoricalScenariosMetricsDisclaimer', {
    startDate: formattedStartingDate,
    endDate: formattedEndingDate,
  });

  const [itemWithDisclaimers, setItemWithDisclaimers] = useState(item);

  useEffect(() => {
    const usesRollingBackfill =
      comparison.selectedHistoricalScenarios?.some((_, scenarioIndex) => {
        const calculatedScenario = calculatedHistoricalScenarios?.[scenarioIndex];
        const hasRollingBackfill = calculatedScenario?.items?.[itemIndex]?.rollingBackfillTimestamp;
        return hasRollingBackfill;
      }) ?? false;

    if (usesRollingBackfill) {
      setItemWithDisclaimers(prevItem => ({
        ...prevItem,
        disclaimers: {
          ...prevItem.disclaimers,
          [DISCLAIMER_KEYS.USING_ROLLING_BACKFILL]: {
            key: DISCLAIMER_KEYS.USING_ROLLING_BACKFILL,
          },
        },
      }));
    } else {
      setItemWithDisclaimers(prevItem => {
        if (prevItem.disclaimers?.[DISCLAIMER_KEYS.USING_ROLLING_BACKFILL]) {
          const { [DISCLAIMER_KEYS.USING_ROLLING_BACKFILL]: _, ...rest } = prevItem.disclaimers;
          return {
            ...prevItem,
            disclaimers: Object.keys(rest).length ? rest : undefined,
          };
        }
        return prevItem;
      });
    }
  }, [itemIndex, calculatedHistoricalScenarios, comparison.selectedHistoricalScenarios, item]);

  return (
    <div className={classNames('historical-scenario-table-cells', className)}>
      <div className="historical-scenario-card-header">
        <HistoricalScenariosComparisonHeader
          comparison={comparison}
          item={itemWithDisclaimers}
          user={user}
          updateComparisonItemColor={updateComparisonItemColor}
          popoverContainerRef={popoverContainerRef}
          scrollbarContainerRef={scrollbarContainerRef}
        />
      </div>

      <ComparisonBaseCell key="empty" className="buffer-cell" />

      <ComparisonBaseCell
        className="even-cell"
        key="historical-scenarios-annualized"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-Charts.Growth.AnnualizedRateOfReturn`}
        id={`cell-hs-keyMetrics-Growth.AnnualizedRateOfReturn-${item?._id}`}
      >
        <PercentageCell value={itemPerformance?.metrics.annualizedReturns} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="odd-cell"
        key="historical-scenarios-gross"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-Charts.Growth.GrossRateOfReturn`}
        id={`cell-hs-keyMetrics-Growth.GrossRateOfReturn-${item?._id}`}
      >
        <PercentageCell value={itemPerformance?.metrics.grossReturns} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="even-cell"
        key="historical-scenarios-stddev"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.StandardDeviation`}
        id={`cell-hs-keyMetrics-RiskMetrics.StandardDeviation-${item?._id}`}
      >
        <StatCell value={itemPerformance?.metrics.standardDeviation} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="odd-cell"
        key="historical-scenarios-mdd"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.MaximumDrawdown`}
        id={`cell-hs-keyMetrics-RiskMetrics.MaximumDrawdown-${item?._id}`}
      >
        <StatCell value={itemPerformance?.drawdownData.maxDrawdown} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="even-cell"
        key="historical-scenarios-dd"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.DrawdownDuration`}
        id={`cell-hs-keyMetrics-RiskMetrics.DrawdownDuration-${item?._id}`}
      >
        <StatCell value={itemPerformance?.drawdownData.averageDrawdownDays} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="odd-cell"
        key="historical-scenarios-rd"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.RecoveryDuration`}
        id={`cell-hs-keyMetrics-RiskMetrics.RecoveryDuration-${item?._id}`}
      >
        <StatCell value={itemPerformance?.drawdownData.averageRecoveryDays} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="even-cell"
        key="historical-scenarios-beta"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.Beta`}
        id={`cell-hs-keyMetrics-RiskMetrics.Beta-${item?._id}`}
      >
        <StatCell value={itemPerformance?.metrics.beta} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="odd-cell"
        role="cell"
        key="historical-scenarios-sharpe"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.SharpeRatio`}
        id={`cell-hs-keyMetrics-RiskMetrics.SharpeRatio-${item?._id}`}
      >
        <StatCell value={itemPerformance?.metrics.sharpeRatio} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="even-cell"
        key="historical-scenarios-sortino"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.SortinoRatio`}
        id={`cell-hs-keyMetrics-RiskMetrics.SortinoRatio-${item?._id}`}
      >
        <StatCell value={itemPerformance?.metrics.sortino} />
      </ComparisonBaseCell>

      <ComparisonBaseCell
        className="odd-cell"
        key="historical-scenarios-updown"
        role="cell"
        ariaLabelledBy={`row-${item?._id} hs-category-header-keyMetrics field-header-RiskMetrics.UpDownCapture`}
        id={`cell-hs-keyMetrics-RiskMetrics.UpDownCapture-${item?._id}`}
      >
        <UpDownCell
          value={{
            upside: itemPerformance?.metrics.upside!,
            downside: itemPerformance?.metrics.downside!,
          }}
        />
      </ComparisonBaseCell>

      <ComparisonBaseCell className="category-header" key="historical-scenarios-disclaimer" role="cell" id={`cell-hs-disclaimer-${item?._id}`}>
        <div className="section-disclaimer">
          <span>{disclaimer}</span>
        </div>
      </ComparisonBaseCell>
    </div>
  );
};

export default HistoricalScenariosComparisonScenario;
